import { PERIOD_KEYS, VAT_MODES, DEFAULT_VAT_MODE, type PeriodKey, type VatMode } from "@apphq/shared/metrics";
import type { Environment } from "@apphq/shared/types";

export type Theme = "auto" | "light" | "dark";
export interface Prefs {
  env: Environment;
  period: PeriodKey;
  vat: VatMode;
  theme: Theme;
}

export const DEFAULT_PREFS: Prefs = { env: "production", period: "30d", vat: DEFAULT_VAT_MODE, theme: "auto" };

/** Validates a stored value against its allowed set; anything unexpected falls back. */
export function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export function parsePrefs(raw: string | null): Prefs {
  if (!raw) return DEFAULT_PREFS;
  let o: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_PREFS;
    o = parsed as Record<string, unknown>;
  } catch {
    return DEFAULT_PREFS;
  }
  return {
    env: DEFAULT_PREFS.env, // pinned: the sandbox toggle is gone, a stale stored "sandbox" must not stick
    period: pick(o.period, PERIOD_KEYS, DEFAULT_PREFS.period),
    vat: pick(o.vat, VAT_MODES, DEFAULT_PREFS.vat),
    theme: pick(o.theme, ["auto", "light", "dark"] as const, DEFAULT_PREFS.theme),
  };
}
